import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { GreenroomState } from '../../stores/types/types';
import type { Shot } from '../../stores/types/shots';
import { SHOT_THUMBNAIL_TYPES } from '../../stores/types/shots';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { ConnectedUser } from '../../stores/types/connection';
import { durationString } from '../../utils/utils';
import useShots from '../../contexts/shots/provider';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import ShotThumbnail from '../general-components/shot-thumbnail';
import AvatarBubbleRow from '../general-components/avatar-bubble-row';

interface Props {
	shot: Shot;
	playStart: Date | null;
}

export default function DraggableShotThumbnail(props: Props) {
	const roomState = useSelector((state: GreenroomState) => state.MeetingReducer.roomState);
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);
	const { connectedUsers, session } = useSelector((state: GreenroomState) => state.SessionReducer);
	const {
		shot,
		shot: {
			shot_data: {
				shotTitle,
				shotDuration,
				order,
				autoplay,
				autoDuration,
			},
		},
		playStart,
	} = props;

	const { currentShot, getShotDuration } = useShots();

	useEffect(() => {
		document.body.style.cursor = 'grabbing';

		return () => {
			document.body.style.cursor = '';
		};
	}, []);

	const usersViewingShot = useMemo(() => connectedUsers.filter((user: ConnectedUser) => (
		user.selectedShowbuilderShot === shot.shot_id
			&& user.session !== session && user.in_room
	)), [connectedUsers]);

	const titleText = useMemo((): string => {
		if (shotTitle) {
			return shotTitle;
		}
		return roomState === ROOM_STATES.SHOWTIME
			? 'Unnamed Shot'
			: 'Enter Shot Title...';
	}, [shotTitle, roomState]);

	const [realDuration, setDuration] = useState(shotDuration);
	useEffect(() => {
		const getAutoDuration = async (feed: number) => {
			setDuration(await getShotDuration(feed));
		};
		if (autoDuration && shot.shot_data.shotFeeds[0].media) {
			getAutoDuration(shot.shot_data.shotFeeds[0].media);
		} else {
			setDuration(shotDuration);
		}
	}, [autoDuration, shot, shotDuration, media]);

	return (
		<div className="shot-thumbnail-wrapper draggable">
			<ShotThumbnail
				open={true}
				selected={currentShot?.shot_id === shot.shot_id}
				playStart={playStart}
				isDragging={true}
				borderRatio={0.25}
				shot={shot}
				shotThumbnailType={SHOT_THUMBNAIL_TYPES.SHOWBOARD}
			/>
			<div className="shot-thumbnail-below">
				<div className="drag-ellipses">
					<Icon name={ICONS.SHOWBOARD_ELLIPSES} color={COLORS.DARK_GRAY} size={12} />
					<span>{order + 1}</span>
				</div>
				<div className="shot-meta">
					<span className={classNames('shot-title', !shotTitle && 'untitled')}>{titleText}</span>
					<div className='flex-align-center'>
						<span className="shot-duration">
							{realDuration ? durationString(realDuration) : ' '}
						</span>
						<div
							style={{ display: autoplay ? 'block' : 'none' }}>
							<Icon
								name={ICONS.SHOT_AUTOPLAY_AUTO}
								size={12}
								color={COLORS.WHITE}
							/>
						</div>
					</div>
				</div>
				{roomState === ROOM_STATES.EDITING && usersViewingShot.length > 0 && (
					<AvatarBubbleRow
						invites={usersViewingShot}
						size={'twentyfour'}
						alignEnd={true}
						expandable={false}
					/>
				)}
			</div>
		</div>
	);
}
